import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

export type WorkspaceNavigationCopy = {
  openPage: string;
  inbox: string;
  myDrive: string;
  freeTier: string;
  starterOffer: string;
  starterOfferTerms: string;
  viewPlans: string;
};

const COPY_BY_LOCALE = {
  en: {
    openPage: 'Open page',
    inbox: 'Connect',
    myDrive: 'My Drive',
    freeTier: 'Free tier',
    starterOffer: 'Starter offer',
    starterOfferTerms: '$20 first month · then $60/month',
    viewPlans: 'View plans',
  },
  'pt-BR': {
    openPage: 'Abrir página',
    inbox: 'Conectar',
    myDrive: 'Minha unidade.',
    freeTier: 'Camada livre',
    starterOffer: 'Oferta inicial.',
    starterOfferTerms: '$20 no primeiro mês · depois $60/mês',
    viewPlans: 'Ver planos',
  },
  ar: {
    openPage: 'فتح الصفحة',
    inbox: 'اتصال',
    myDrive: 'ملفاتي',
    freeTier: 'الخطة المجانية',
    starterOffer: 'عرض Starter',
    starterOfferTerms: '20$ للشهر الأول · ثم 60$/شهريًا',
    viewPlans: 'عرض الخطط',
  },
  es: {
    openPage: 'Abrir página',
    inbox: 'Conectar',
    myDrive: 'Mi unidad',
    freeTier: 'Plan gratuito',
    starterOffer: 'Oferta Starter',
    starterOfferTerms: '$20 el primer mes · luego $60/mes',
    viewPlans: 'Ver planes',
  },
  fr: {
    openPage: 'Ouvrir la page',
    inbox: 'Connexion',
    myDrive: 'Mon Drive',
    freeTier: 'Offre gratuite',
    starterOffer: 'Offre Starter',
    starterOfferTerms: '20 $ le premier mois · puis 60 $/mois',
    viewPlans: 'Voir les offres',
  },
  it: {
    openPage: 'Apri pagina',
    inbox: 'Connetti',
    myDrive: 'Il mio Drive',
    freeTier: 'Piano gratuito',
    starterOffer: 'Offerta Starter',
    starterOfferTerms: '$20 il primo mese · poi $60/mese',
    viewPlans: 'Vedi i piani',
  },
  ko: {
    openPage: '페이지 열기',
    inbox: '연결',
    myDrive: '내 드라이브',
    freeTier: '무료 요금제',
    starterOffer: 'Starter 혜택',
    starterOfferTerms: '첫 달 $20 · 이후 월 $60',
    viewPlans: '요금제 보기',
  },
  de: {
    openPage: 'Seite öffnen',
    inbox: 'Verbinden',
    myDrive: 'Meine Ablage',
    freeTier: 'Kostenloser Tarif',
    starterOffer: 'Starter-Angebot',
    starterOfferTerms: '20 $ im ersten Monat · danach 60 $/Monat',
    viewPlans: 'Tarife ansehen',
  },
  ja: {
    openPage: 'ページを開く',
    inbox: '接続',
    myDrive: 'マイドライブ',
    freeTier: '無料プラン',
    starterOffer: 'Starter オファー',
    starterOfferTerms: '初月 $20 · 以降は月額 $60',
    viewPlans: 'プランを見る',
  },
  hi: {
    openPage: 'पेज खोलें',
    inbox: 'कनेक्ट करें',
    myDrive: 'मेरी ड्राइव',
    freeTier: 'मुफ़्त प्लान',
    starterOffer: 'Starter ऑफ़र',
    starterOfferTerms: 'पहला महीना $20 · फिर $60/माह',
    viewPlans: 'प्लान देखें',
  },
  ru: {
    openPage: 'Открыть страницу',
    inbox: 'Подключение',
    myDrive: 'Мой диск',
    freeTier: 'Бесплатный тариф',
    starterOffer: 'Предложение Starter',
    starterOfferTerms: '20 $ за первый месяц · затем 60 $/мес.',
    viewPlans: 'Посмотреть тарифы',
  },
  zh: {
    openPage: '打开页面',
    inbox: '连接',
    myDrive: '我的云端硬盘',
    freeTier: '免费方案',
    starterOffer: 'Starter 优惠',
    starterOfferTerms: '首月 $20 · 之后 $60/月',
    viewPlans: '查看方案',
  },
  'zh-TW': {
    openPage: '開啟頁面',
    inbox: '連線',
    myDrive: '我的雲端硬碟',
    freeTier: '免費方案',
    starterOffer: 'Starter 優惠',
    starterOfferTerms: '首月 $20 · 之後 $60/月',
    viewPlans: '查看方案',
  },
  vi: {
    openPage: 'Mở trang',
    inbox: 'Kết nối',
    myDrive: 'Drive của tôi',
    freeTier: 'Gói miễn phí',
    starterOffer: 'Ưu đãi Starter',
    starterOfferTerms: 'Tháng đầu $20 · sau đó $60/tháng',
    viewPlans: 'Xem các gói',
  },
  id: {
    openPage: 'Buka halaman',
    inbox: 'Hubungkan',
    myDrive: 'Drive Saya',
    freeTier: 'Paket gratis',
    starterOffer: 'Penawaran Starter',
    starterOfferTerms: '$20 bulan pertama · lalu $60/bulan',
    viewPlans: 'Lihat paket',
  },
} satisfies Record<SupportedLocale, WorkspaceNavigationCopy>;

export function getWorkspaceNavigationCopy(
  locale: string
): WorkspaceNavigationCopy {
  return COPY_BY_LOCALE[normalizeLocale(locale)];
}
